import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Spin, notification } from "antd";
import { AudioOutlined, LoadingOutlined, StopOutlined } from "@ant-design/icons";
import { attachBrowserConversationWhenReady, fetchTestCall, startBrowserTest } from "pages/Sonic/service";
import { acquireMicrophone, startBrowserVoiceSession } from "pages/Sonic/browserVoiceSession";
import { stopSpeechPreview } from "pages/Sonic/voicePreview";
import { resolveSonicApiError } from "pages/Sonic/sonicApiError";
import CallConversation from "pages/FeedbackCapture/responses/CallConversation";
import { Notice } from "./phoneTriggerShared";
import { loadTestValues, mergeFieldsOf, saveTestValues, withTestValues } from "./voiceTestValues";
import { withFields } from "./VoiceSurveyPreview";
import styles from "./style.module.scss";

const POLL_MS = 3000;
const DONE_STATES = ["completed", "failed"];
const TEST_ERROR_KEYS = {
  provider_error: "sonic.call_error_provider",
  test_call_limit: "sonic.call_error_limit",
  elevenlabs_not_configured: "sonic.voice_coming_soon",
};

/**
 * "Talk in the browser": the survey's agent on the microphone, as a customer would hear it on the
 * phone, with typed values for the texts' merge fields. Its answers land on the survey. Used in the
 * Distribute step (with unsaved edits) and in the survey list's preview (the saved survey).
 */
const VoiceBrowserTest = ({ instrumentId, surveyTitle, questions, phoneCall, language, hint, children }) => {
  const { t, i18n } = useTranslation();
  const [starting, setStarting] = useState(false);
  const [live, setLive] = useState(false);
  const [lines, setLines] = useState([]);
  const [call, setCall] = useState(null);
  const [testValues, setTestValues] = useState(loadTestValues);
  const mountedRef = useRef(true);
  const sessionRef = useRef(null);
  const pollRef = useRef(null);
  const brand = (phoneCall.brandName || "").trim();
  const greeting = (phoneCall.greeting || "").trim();
  const testFields = mergeFieldsOf(
    phoneCall.greeting,
    phoneCall.context,
    phoneCall.disclosure,
    phoneCall.knowledge,
    ...(questions || []).flatMap((q) => [q && q.text, q && q.agentNote])
  );

  const setTestValue = (name, value) => {
    const next = { ...testValues, [name]: value };
    setTestValues(next);
    saveTestValues(next);
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  useEffect(
    () => () => {
      mountedRef.current = false;
      stopPolling();
      if (sessionRef.current) sessionRef.current.stop();
    },
    []
  );

  // The transcript and the survey response arrive once ElevenLabs has analysed the call.
  const pollCall = (callId) => {
    stopPolling();
    const tick = async () => {
      const res = await fetchTestCall(callId);
      if (!mountedRef.current || !res || res.error || !res.call) return;
      setCall(res.call);
      if (DONE_STATES.includes(res.call.status)) stopPolling();
    };
    pollRef.current = setInterval(tick, POLL_MS);
    tick();
  };

  const runTest = async () => {
    stopSpeechPreview();
    setCall(null);
    setLines([]);
    setStarting(true);
    let stream;
    try {
      stream = await acquireMicrophone();
    } catch (e) {
      setStarting(false);
      notification.error({ message: t("sonic.browser_mic_denied") });
      return;
    }
    const res = await startBrowserTest({
      instrument_id: instrumentId,
      language,
      report_language: i18n.language,
      persona: phoneCall.persona,
      context: (phoneCall.context || "").trim(),
      greeting,
      disclosure: (phoneCall.disclosure || "").trim(),
      knowledge: (phoneCall.knowledge || "").trim(),
      voice_id: phoneCall.voiceId || undefined,
      brand_name: brand || undefined,
    });
    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    setStarting(false);
    if (!res || res.error || !res.signed_url) {
      stream.getTracks().forEach((track) => track.stop());
      const key = TEST_ERROR_KEYS[res?.reason];
      notification.error({ message: key ? t(key) : resolveSonicApiError(res, t, "sonic.action_failed") });
      return;
    }
    const callId = res.call.id;
    setCall(res.call);
    setLive(true);
    sessionRef.current = startBrowserVoiceSession({
      stream,
      signedUrl: res.signed_url,
      dynamicVariables: withTestValues(res.dynamic_variables, testValues),
      onConversationId: (conversationId) => attachBrowserConversationWhenReady(callId, conversationId),
      onLine: (role, text) => {
        if (text) setLines((prev) => [...prev, { key: String(prev.length), role, text }]);
      },
      onEnd: () => {
        sessionRef.current = null;
        if (!mountedRef.current) return;
        setLive(false);
        pollCall(callId);
      },
      onError: () => notification.error({ message: t("sonic.browser_connection_error") }),
    });
  };

  const speaker = (role) => (role === "agent" ? t("sonic.browser_agent") : t("sonic.browser_you"));

  const renderResult = () => {
    if (live) {
      return lines.length ? (
        <div className={styles.phoneTestLines}>
          {lines.map((line) => (
            <div key={line.key}>
              <span className={styles.phoneTestSpeaker}>{speaker(line.role)}:</span> {line.text}
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.fieldHint}>{t("sonic.browser_listening")}</p>
      );
    }
    if (!call) return null;
    if (!DONE_STATES.includes(call.status)) {
      return (
        <div className={styles.fieldHint}>
          <Spin size="small" indicator={<LoadingOutlined spin />} /> {t("sonic.browser_analyzing")}
        </div>
      );
    }
    if (call.status === "failed") {
      return <Notice error>{t("sonic.call_failed")}</Notice>;
    }
    if (call.survey_response_id) {
      return (
        <>
          <Notice>{t("surveyManagement.distribute.phone.test_saved")}</Notice>
          <CallConversation
            instrumentId={instrumentId}
            response={{ id: call.survey_response_id }}
            number="test"
            surveyTitle={surveyTitle}
          />
        </>
      );
    }
    return (
      <div className={styles.phoneTestLines}>
        {(call.turns || []).map((turn, index) => (
          <div key={String(index)}>
            <span className={styles.phoneTestSpeaker}>{speaker(turn.role)}:</span> {turn.text}
          </div>
        ))}
      </div>
    );
  };

  const busy = starting || live || (call && !DONE_STATES.includes(call.status));

  return (
    <div className={styles.phoneFormat}>
      <div className={styles.phoneTestHeader}>
        <div>
          <div className={styles.phoneFormatTitle}>
            <AudioOutlined /> {t("sonic.test_mode_browser")}
          </div>
          <p className={styles.fieldHint}>
            {instrumentId ? hint || t("surveyManagement.distribute.phone.test_hint") : t("surveyManagement.distribute.phone.test_save_first")}
          </p>
        </div>
        {live ? (
          <Button danger icon={<StopOutlined />} onClick={() => sessionRef.current && sessionRef.current.stop()}>
            {t("sonic.browser_stop")}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<AudioOutlined />}
            loading={starting}
            disabled={!instrumentId || !!busy}
            onClick={runTest}
          >
            {t("sonic.browser_start")}
          </Button>
        )}
      </div>
      {testFields.length ? (
        <div className={styles.phoneTestValues}>
          <div className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.test_values")}</div>
          <p className={styles.fieldHint}>{t("surveyManagement.distribute.phone.test_values_hint")}</p>
          <div className={styles.distGrid2}>
            {testFields.map((name) => (
              <div key={name} className={styles.distField}>
                <label className={styles.fieldLabel} htmlFor={`voice-test-${name}`}>{`{{${name}}}`}</label>
                <Input
                  id={`voice-test-${name}`}
                  value={testValues[name] || ""}
                  maxLength={200}
                  disabled={!!busy}
                  placeholder={t("surveyManagement.distribute.phone.test_value_ph")}
                  onChange={(e) => setTestValue(name, e.target.value)}
                />
              </div>
            ))}
          </div>
          {greeting ? (
            <p className={styles.phoneTestOpening}>
              <span className={styles.phoneTestSpeaker}>{t("surveyManagement.distribute.phone.test_opening")}</span>{" "}
              {withFields(greeting, brand ? { ...testValues, brand } : testValues)}
            </p>
          ) : null}
        </div>
      ) : null}
      {children}
      {renderResult()}
    </div>
  );
};

export default VoiceBrowserTest;
